/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const KI_MARKER_REGEXP = /\[\s*ki\s*\]/i;
  const KI_MARKER_REPLACE_REGEXP = /\[\s*ki\s*\]/gi;
  const body = document.body;

  if (!body) {
    return;
  }

  const badgeEnabled = body.dataset.kiBadgeEnabled === "true";
  const imageMarkerState = new WeakMap();
  const internalAltUpdates = new WeakSet();
  const getBadgeText = () => body.dataset.kiBadgeText && body.dataset.kiBadgeText.trim() ? body.dataset.kiBadgeText.trim() : "KI";
  const hasKiMarker = (value) => KI_MARKER_REGEXP.test(value || "");

  const cleanAltText = (value) => value
    .replace(KI_MARKER_REPLACE_REGEXP, " ")
    .replace(/\s+/g, " ")
    .trim();

  const prepareImage = (image) => {
    if (imageMarkerState.has(image)) {
      return imageMarkerState.get(image);
    }

    const altValue = image.getAttribute("alt") || "";
    const marked = hasKiMarker(altValue);
    const cleanedAlt = cleanAltText(altValue);

    imageMarkerState.set(image, marked);

    if (altValue !== cleanedAlt) {
      internalAltUpdates.add(image);
      image.setAttribute("alt", cleanedAlt);
    }

    return marked;
  };

  const findBadgeTarget = (element) => {
    const productMediaItem = element.closest(".product__media-item");

    if (productMediaItem) {
      return productMediaItem;
    }

    const productItemMedia = element.closest(".product-item__aspect-ratio");

    if (productItemMedia) {
      return productItemMedia;
    }

    if (element.hasAttribute("data-ki-alt") && !element.matches("img")) {
      return element;
    }

    return element.closest(".video-wrapper, picture, figure, .aspect-ratio, a") || element.parentElement;
  };

  const createBadge = () => {
    const badgeElement = document.createElement("span");
    badgeElement.className = "label ki-badge";
    badgeElement.textContent = getBadgeText();
    return badgeElement;
  };

  const getDirectBadges = (target) => Array.from(target.children).filter((child) => child.classList.contains("ki-badge"));

  const targetHasKiMarker = (target, targetImages) => {
    if (!badgeEnabled) {
      return false;
    }

    const targetAlt = target.getAttribute("data-ki-alt");

    if (hasKiMarker(targetAlt)) {
      return true;
    }

    if (target.matches(".product-item__aspect-ratio")) {
      const activeImage = target.querySelector(".product-item__primary-image:not([hidden])") || target.querySelector(".product-item__primary-image");
      return activeImage ? prepareImage(activeImage) : false;
    }

    return targetImages.some((image) => !image.hidden && prepareImage(image));
  };

  const reconcileBadge = (target, targetImages) => {
    const directBadges = getDirectBadges(target);

    if (targetHasKiMarker(target, targetImages)) {
      target.classList.add("ki-badge-target");

      if (directBadges.length === 0) {
        target.appendChild(createBadge());
      } else {
        directBadges.slice(1).forEach((badge) => badge.remove());
      }

      return;
    }

    directBadges.forEach((badge) => badge.remove());
    target.classList.remove("ki-badge-target");
  };

  const applyBadges = () => {
    const images = Array.from(document.querySelectorAll("img[alt]"));
    const mediaWithAltData = Array.from(document.querySelectorAll("[data-ki-alt]"));
    const targets = new Set(document.querySelectorAll(".ki-badge-target"));
    const targetImages = new Map();

    images.forEach((image) => {
      prepareImage(image);

      const target = findBadgeTarget(image);

      if (target) {
        targets.add(target);
        targetImages.set(target, [...(targetImages.get(target) || []), image]);
      }
    });

    mediaWithAltData.forEach((element) => {
      const target = findBadgeTarget(element);

      if (target) {
        targets.add(target);
      }
    });

    targets.forEach((target) => reconcileBadge(target, targetImages.get(target) || []));
  };

  let rafId = null;
  const scheduleBadgeScan = () => {
    if (rafId) {
      return;
    }

    rafId = requestAnimationFrame(() => {
      applyBadges();
      rafId = null;
    });
  };

  applyBadges();
  document.addEventListener("shopify:section:load", scheduleBadgeScan);
  document.addEventListener("variant:changed", scheduleBadgeScan);

  if ("MutationObserver" in window) {
    const observer = new MutationObserver((mutations) => {
      let shouldScan = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          shouldScan = true;
        }

        if (mutation.type === "attributes") {
          if (mutation.attributeName === "alt") {
            if (internalAltUpdates.has(mutation.target)) {
              internalAltUpdates.delete(mutation.target);
              return;
            }

            imageMarkerState.delete(mutation.target);
          }

          shouldScan = true;
        }
      });

      if (shouldScan) {
        scheduleBadgeScan();
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["alt", "data-ki-alt", "hidden"],
      childList: true,
      subtree: true
    });
  }
})();

(() => {
  const PRODUCT_FORM_SELECTOR = 'form[is="product-form"]';
  let cartVariantCounts = new Map();
  let animationFrameId = null;
  const isPromoForm = (form) => Boolean(form.querySelector('[data-aktionsartikel-lock-marker]'));

  const getSubmitButtons = (form) =>
    Array.from(form.elements).filter((element) => element.tagName === 'BUTTON' && element.type === 'submit');

  const setButtonText = (button, text) => {
    if (!text) {
      return;
    }

    if (button.getAttribute('is') === 'loader-button' && button.firstElementChild) {
      button.firstElementChild.innerHTML = text;
      return;
    }

    const srOnlyText = button.querySelector('.visually-hidden');

    if (srOnlyText && button.classList.contains('product-item__quick-buy-button')) {
      srOnlyText.innerHTML = text;
      return;
    }

    button.innerHTML = text;
  };

  const setCartState = (cart) => {
    const nextCounts = new Map();
    const items = Array.isArray(cart?.items) ? cart.items : [];

    items.forEach((item) => {
      const variantId = Number.parseInt(item.id, 10);

      if (Number.isFinite(variantId)) {
        nextCounts.set(variantId, (nextCounts.get(variantId) || 0) + item.quantity);
      }
    });

    cartVariantCounts = nextCounts;
  };

  const updatePromoFormState = (form) => {
    const variantInput = form.querySelector('input[name="id"]');

    if (!variantInput) {
      return;
    }

    const variantId = Number.parseInt(variantInput.value, 10);

    if (!Number.isFinite(variantId)) {
      return;
    }

    const isVariantAlreadyInCart = (cartVariantCounts.get(variantId) || 0) > 0;

    getSubmitButtons(form).forEach((button) => {
      if (isVariantAlreadyInCart) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
        button.dataset.aktionsartikelLocked = 'true';
        setButtonText(button, button.dataset.inCartContent);
        return;
      }

      if (button.dataset.aktionsartikelLocked === 'true') {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        button.dataset.aktionsartikelLocked = 'false';
        setButtonText(button, button.dataset.buttonContent);
      }
    });
  };

  const applyPromoLocks = () => {
    document.querySelectorAll(PRODUCT_FORM_SELECTOR).forEach((form) => {
      if (isPromoForm(form)) {
        updatePromoFormState(form);
      }
    });
  };

  const schedulePromoLockUpdate = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }

    animationFrameId = requestAnimationFrame(() => {
      animationFrameId = null;
      applyPromoLocks();
    });
  };

  const refreshCartState = async () => {
    const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart';
    const response = await fetch(`${cartUrl}.js`, { credentials: 'same-origin' });
    const cart = await response.json();
    setCartState(cart);
    schedulePromoLockUpdate();
  };

  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.cart) {
      setCartState(event.detail.cart);
      schedulePromoLockUpdate();
    }
  });

  document.addEventListener('variant:changed', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !isPromoForm(form)) {
      return;
    }

    requestAnimationFrame(() => updatePromoFormState(form));
  });

  document.addEventListener('shopify:section:load', schedulePromoLockUpdate);

  const observer = new MutationObserver((mutations) => {
    const hasPromoFormInsertion = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) {
          return false;
        }

        if (node.matches?.(PRODUCT_FORM_SELECTOR) && isPromoForm(node)) {
          return true;
        }

        const nestedProductForms = Array.from(node.querySelectorAll?.(PRODUCT_FORM_SELECTOR) || []);
        return nestedProductForms.some(isPromoForm);
      })
    );

    if (hasPromoFormInsertion) {
      schedulePromoLockUpdate();
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  refreshCartState().catch(() => {
    schedulePromoLockUpdate();
  });
})();

/**
 * Hinweis-Einschub (sections/notice-drawer.liquid)
 *
 * Active when the global "Cart type" setting is "notice_drawer". After a successful add to cart the drawer shows
 * the added product, a fresh free shipping bar and recommendations for that product ("complementary", with an
 * optional fallback to "related"). The theme's success toast is suppressed; errors still show as before.
 * Requirements: docs/anforderungen/add-to-cart-drawer.md
 */
(() => {
  if (window.themeVariables?.settings?.cartType !== 'notice_drawer' || typeof DrawerContent === 'undefined') {
    return;
  }

  const getDrawer = () => document.getElementById('notice-drawer');

  const fetchSectionDocument = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin' });

    if (!response.ok) {
      throw new Error(`Hinweis-Einschub: ${response.status} for ${url}`);
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  class NoticeDrawer extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
      this._requestId = 0;

      // A quick view opened from a recommendation removes "lock-all" when it closes, although this drawer is still open
      this.addEventListener('openable-element:close', (event) => {
        if (event.target !== this && this.open) {
          document.documentElement.classList.add('lock-all');
        }
      });

      if (window.Shopify?.designMode && this.dataset.previewProductId) {
        this._refresh(this.dataset.previewProductId, ++this._requestId).catch(() => {});
      }
    }

    get sectionId() {
      return this.getAttribute('section');
    }

    get recommendationsSource() {
      return this.dataset.recommendationsSource || 'complementary_fallback';
    }

    async showFor(lineItem) {
      const requestId = ++this._requestId;
      const productElement = this.querySelector('[data-notice-product]');

      if (productElement) {
        productElement.textContent = lineItem.variant_title ? `${lineItem.product_title} – ${lineItem.variant_title}` : lineItem.product_title;
      }

      // Recommendations of the previously added product must not show while the new ones load
      const currentRecommendations = this.querySelector('notice-drawer-recommendations');

      if (currentRecommendations) {
        currentRecommendations.hidden = true;
      }

      this.open = true;

      try {
        await this._refresh(lineItem.product_id, requestId);
      } catch (error) {
        console.error(error);
      }
    }

    get recommendationsCount() {
      return parseInt(this.dataset.recommendationsCount, 10) || 5;
    }

    async _fetchRecommendations(productId, intent) {
      const routes = window.themeVariables.routes;
      const sectionDocument = await fetchSectionDocument(`${routes.productRecommendationsUrl}?product_id=${productId}&limit=10&intent=${intent}&section_id=${this.sectionId}`);

      return {
        sectionDocument,
        recommendations: sectionDocument.querySelector('notice-drawer-recommendations')
      };
    }

    async _refresh(productId, requestId) {
      let sectionDocument;
      let recommendations = null;

      if (this.hasAttribute('data-show-recommendations')) {
        const source = this.recommendationsSource;
        const first = await this._fetchRecommendations(productId, source === 'related' ? 'related' : 'complementary');

        sectionDocument = first.sectionDocument;
        recommendations = first.recommendations;

        // The section renders every candidate (API limit 10, products in the cart already filtered out) and the count
        // is applied here after merging, so duplicates between complementary and related don't reduce the number shown
        const items = Array.from(recommendations?.querySelectorAll('[data-product-id]') || []);

        if (source === 'complementary_fallback' && items.length < this.recommendationsCount) {
          const related = await this._fetchRecommendations(productId, 'related');
          const shownIds = new Set(items.map((item) => item.dataset.productId));
          const fillItems = Array.from(related.recommendations?.querySelectorAll('[data-product-id]') || [])
            .filter((item) => !shownIds.has(item.dataset.productId))
            .slice(0, this.recommendationsCount - items.length);

          if (items.length === 0) {
            // No complementary products at all: use the related response as it is (heading included)
            recommendations = related.recommendations;
          } else {
            recommendations.querySelector('.notice-drawer__recommendations-list')?.append(...fillItems);
          }
        }

        Array.from(recommendations?.querySelectorAll('[data-product-id]') || [])
          .slice(this.recommendationsCount)
          .forEach((item) => item.remove());
      } else {
        sectionDocument = await fetchSectionDocument(`${window.themeVariables.routes.cartUrl}?section_id=${this.sectionId}`);
      }

      // A newer add to cart started while this one was loading
      if (requestId !== this._requestId) {
        return;
      }

      const newShipping = sectionDocument.querySelector('[data-notice-shipping]');
      const currentShipping = this.querySelector('[data-notice-shipping]');

      if (newShipping && currentShipping) {
        const currentBar = currentShipping.querySelector('free-shipping-bar');
        const newBar = newShipping.querySelector('free-shipping-bar');

        // Focal's <free-shipping-bar> adds a "cart:updated" listener on <html> and never removes it, so an existing
        // bar is updated in place instead of being replaced on every add to cart
        if (currentBar && newBar) {
          currentBar.setAttribute('threshold', newBar.getAttribute('threshold'));
          currentBar.setAttribute('style', newBar.getAttribute('style') || '');
          currentBar.innerHTML = newBar.innerHTML;
        } else {
          currentShipping.replaceWith(newShipping);
        }
      }

      const currentRecommendations = this.querySelector('notice-drawer-recommendations');

      if (currentRecommendations) {
        const hasProducts = Boolean(recommendations && recommendations.querySelector('[data-product-id]'));
        currentRecommendations.innerHTML = hasProducts ? recommendations.innerHTML : '';
        currentRecommendations.hidden = !hasProducts;
      }
    }
  }

  window.customElements.define('notice-drawer', NoticeDrawer);

  // The theme only hides its success toast for the "drawer" cart type. Capturing on window runs before the theme's
  // listeners on <html>, so the toast is stopped for successes only; errors keep their current behaviour.
  window.addEventListener('cart-notification:show', (event) => {
    if (event.detail?.status === 'success' && getDrawer()) {
      event.stopPropagation();
    }
  }, true);

  document.addEventListener('variant:added', (event) => {
    const drawer = getDrawer();
    const lineItem = event.detail?.variant;

    if (!drawer || !lineItem) {
      return;
    }

    // The quick view drawer stays open after adding (the popover closes itself)
    const quickBuyDrawer = event.target.closest?.('quick-buy-drawer');

    if (quickBuyDrawer && quickBuyDrawer.open) {
      quickBuyDrawer.open = false;
    }

    drawer.showFor(lineItem);
  });
})();

/**
 * Qikify Volume Discount bridge
 *
 * On products with a Qikify volume offer, the app intercepts the add to cart click, adds the items itself and then
 * looks for Dawn's cart drawer API (cart-drawer.renderContents). Focal has none, so the app redirects to /cart and
 * the theme's "Cart type" setting is ignored. The app reads window.QikifyVolume on every call: with
 * preventDirectToCartPage it hands the /cart/add.js response to our renderCartContents instead, which fires the same
 * events as Focal's product form. This hook is not documented by Qikify, check it after app updates.
 */
(() => {
  const cartType = window.themeVariables?.settings?.cartType;

  // For the cart page type and on the cart page itself, Qikify's own redirect is the wanted behaviour (same as Focal)
  if (!cartType || cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
    return;
  }

  const getProductForm = () =>
    document.querySelector('form[is="product-form"][qvl-form-listener="true"]') ||
    document.querySelector('form[is="product-form"]');

  // A failed refresh must not send a customer to /cart who is already navigating elsewhere
  let isLeavingPage = false;
  window.addEventListener('pagehide', () => {
    isLeavingPage = true;
  });

  // A page restored from the back-forward cache is active again
  window.addEventListener('pageshow', () => {
    isLeavingPage = false;
  });

  const toJson = (response) => {
    if (!response.ok) {
      throw new Error(`Qikify bridge: ${response.status} for ${response.url}`);
    }

    return response.json();
  };

  const renderCartContents = async (response) => {
    const cartUrl = window.themeVariables.routes.cartUrl;
    const form = getProductForm() || document.documentElement;
    const lineItem = response?.items?.[0];

    const goToCart = () => {
      if (!isLeavingPage) {
        window.location.href = cartUrl;
      }
    };

    // On a 422 Qikify passes error.detail, which Shopify's cart error doesn't have, so there is no message to show.
    // Keep the app's previous behaviour and let the cart page show the current state.
    if (!lineItem) {
      goToCart();
      return;
    }

    // The item is already in the cart at this point. If the cart can't be refreshed, the cart page shows the current
    // state instead of leaving the customer without any feedback. A section that fails to render comes back as null.
    let cart;
    let sections;

    try {
      [cart, sections] = await Promise.all([
        fetch(`${cartUrl}.js`).then(toJson),
        fetch(`${cartUrl}?sections=mini-cart`).then(toJson)
      ]);

      const miniCartHtml = sections?.['mini-cart'];

      if (typeof miniCartHtml !== 'string' || !miniCartHtml.includes('<cart-drawer')) {
        throw new Error('Qikify bridge: mini-cart section missing');
      }
    } catch (error) {
      console.warn(error);
      goToCart();
      return;
    }

    // Dispatched only after a successful refresh, so nothing opens before a possible redirect. Errors thrown by
    // listeners don't reach dispatchEvent's caller, so these stay outside the try block.
    form.dispatchEvent(new CustomEvent('variant:added', {
      bubbles: true,
      detail: { variant: lineItem }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        cart: { ...cart, sections },
        openMiniCart: cartType === 'drawer' && !form.closest?.('.drawer')
      }
    }));

    form.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: { status: 'success', error: '' }
    }));
  };

  window.QikifyVolume = {
    ...(window.QikifyVolume || {}),
    preventDirectToCartPage: true,
    renderCartContents
  };
})();
